import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { selectUser } from "../../../features/userSlice";
import { useIsDarkMode } from "../../../theme/ThemeContext";
import { Card, PageHeader, PageShell, SectionHeader } from "../../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../../components/UI/States";
import { Notice, Pill, SearchField, Segmented } from "../../../components/Fleet/FleetParts";
import { formatDateTime } from "../../../components/Fleet/fleetFormat";
import {
  SUBMITTED_LABELS,
  commandLabel,
  fetchAssistantRequests,
  fetchAssistantUsage,
} from "./assistantUsageApi";

// How people use the Node Mobile assistant and Siri ("Hey Siri, ask Node"),
// and what they ask for that it can't do yet. Admin only. Data comes from the
// mobile app's server, which logs every request (users.assistant_logs).

const RANGES = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: "all", label: "All time" },
];

const REQUEST_FILTERS = [
  { value: "all", label: "All" },
  { value: "siri", label: "Siri" },
  { value: "unsupported", label: "Couldn't do" },
  { value: "nothing", label: "No command" },
  { value: "problems", label: "Problems" },
];

// Bar colors: the app in sky, Siri in violet, matching their pills.
const CHANNEL_COLORS = { app: "#0ea5e9", siri: "#8b5cf6" };

const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;
const percent = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

const listNames = (names, shown = 3) =>
  names.length <= shown
    ? names.join(", ")
    : `${names.slice(0, shown).join(", ")} and ${names.length - shown} more`;

const shortDay = (key) =>
  new Date(`${key}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const ChannelPill = ({ channel }) =>
  channel === "siri" ? <Pill tone="violet">Siri</Pill> : <Pill tone="sky">App</Pill>;

// ------------------------------------------------------------------ chart

const PerDayChart = ({ byDay }) => {
  const isDark = useIsDarkMode();
  const data = byDay.map((d) => ({ ...d, label: shortDay(d.day) }));
  return (
    <Card className="mb-6">
      <SectionHeader
        title="Requests per day"
        description="Each bar is one day (Eastern time), split by where the request came from."
      />
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isDark ? "#33415566" : "#e2e8f0"}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: isDark ? "#33415544" : "#f1f5f9" }}
              // Same floating material as menus/popovers in every theme.
              contentStyle={{
                backgroundColor: "var(--app-popover-bg)",
                border: "1px solid var(--app-popover-border)",
                borderRadius: 12,
                boxShadow: "var(--app-popover-shadow)",
                WebkitBackdropFilter: "var(--app-glass-filter)",
                backdropFilter: "var(--app-glass-filter)",
                color: "var(--app-text)",
                fontSize: 12,
              }}
              labelStyle={{ color: "var(--app-text)", fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="app" name="In the app" stackId="requests" fill={CHANNEL_COLORS.app} />
            <Bar
              dataKey="siri"
              name="Siri"
              stackId="requests"
              fill={CHANNEL_COLORS.siri}
              radius={[3, 3, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// ------------------------------------------------------------ couldn't do

const UnsupportedPanel = ({ groups, ranNothing, onShowRequests }) => (
  <>
    <Card padded={false} className="overflow-hidden">
      {groups.length === 0 ? (
        <EmptyState
          compact
          icon={LightbulbOutlinedIcon}
          title="Nothing it couldn't do in this period."
          description="When someone asks the assistant for something it has no command for, it's listed here, grouped by what they wanted, with the most-asked first."
        />
      ) : (
        <ul className="divide-y divide-[color:var(--app-divider)]">
          {groups.map((group) => (
            <li key={group.request} className="px-4 py-3 sm:px-5">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-semibold first-letter:uppercase">{group.request}</span>
                <Pill tone="amber">{plural(group.count, "ask")}</Pill>
                {group.siri > 0 && <Pill tone="violet">{group.siri} through Siri</Pill>}
              </span>
              <span className="app-muted block text-xs">
                {group.people.length ? `Asked by ${listNames(group.people)} · ` : ""}
                last {formatDateTime(group.lastAt)}
              </span>
              {group.examples.map((example) => (
                <span key={example} className="app-muted mt-1 block text-sm italic">
                  “{example}”
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}
    </Card>
    {ranNothing > 0 && (
      <p className="app-muted mt-3 text-sm">
        {plural(ranNothing, "more request was", "more requests were")} answered without running any
        command: small talk, or something it couldn't do but didn't flag.{" "}
        <button type="button" className="app-link font-semibold" onClick={onShowRequests}>
          See them
        </button>
      </p>
    )}
  </>
);

// --------------------------------------------------------------- requests

const RequestRow = ({ row }) => (
  <li className="px-4 py-3 sm:px-5">
    <span className="flex flex-wrap items-center gap-2">
      {row.anonymous ? (
        <span className="app-muted inline-flex items-center gap-1 font-semibold">
          <LockOutlinedIcon sx={{ fontSize: 16 }} />
          Anonymous
        </span>
      ) : (
        <span className="font-semibold">{row.name || "Unknown"}</span>
      )}
      <ChannelPill channel={row.channel} />
      <span className="app-muted text-xs sm:ml-auto">
        {formatDateTime(row.at)}
        {row.seconds ? ` · ${row.seconds}s` : ""}
      </span>
    </span>
    {row.anonymous ? (
      <span className="app-muted mt-1 block text-sm">
        An Order Problem request. These are anonymous, so who asked and what they said aren't kept.
      </span>
    ) : (
      <>
        {row.text && <span className="mt-1 block text-sm">“{row.text}”</span>}
        {row.reply && <span className="app-muted mt-0.5 block text-sm">{row.reply}</span>}
      </>
    )}
    {(row.commands.length > 0 ||
      row.actions.length > 0 ||
      row.unsupported ||
      row.submitted.length > 0 ||
      row.corrected) && (
      <span className="mt-2 flex flex-wrap gap-1.5">
        {row.unsupported && <Pill tone="amber">Couldn't do: {row.unsupported}</Pill>}
        {row.commands
          .filter((c) => c.name !== "assistant_note_unsupported")
          .map((c, i) => (
            <Pill key={`${c.name}-${i}`} tone={c.ok ? "slate" : "rose"}>
              {commandLabel(c.name)}
              {c.ok ? "" : " (failed)"}
            </Pill>
          ))}
        {row.actions.map((action) => (
          <Pill key={action} tone="sky">
            {action}
          </Pill>
        ))}
        {row.submitted.map((kind, i) => (
          <Pill key={`${kind}-${i}`} tone="emerald">
            Submitted {SUBMITTED_LABELS[kind] || "it"}
          </Pill>
        ))}
        {row.corrected && <Pill tone="rose">Claimed something it hadn't done</Pill>}
      </span>
    )}
    {row.error && (
      <span className="mt-1 block text-sm text-rose-600 dark:text-rose-300">Error: {row.error}</span>
    )}
  </li>
);

const RequestsPanel = ({ userId, days, filter, onFilter }) => {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Search once they pause typing.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!userId) return undefined;
    let live = true;
    setRows(null);
    setError(null);
    fetchAssistantRequests(userId, { days, filter, q: search })
      .then((data) => {
        if (!live) return;
        setRows(data.requests);
        setHasMore(data.hasMore);
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [userId, days, filter, search]);

  const loadMore = () => {
    if (!rows?.length) return;
    setLoadingMore(true);
    fetchAssistantRequests(userId, { days, filter, q: search, before: rows[rows.length - 1].at })
      .then((data) => {
        setRows((current) => [...current, ...data.requests]);
        setHasMore(data.hasMore);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMore(false));
  };

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchField value={query} onChange={setQuery} placeholder="Search person or words…" />
        <Segmented
          options={REQUEST_FILTERS}
          value={filter}
          onChange={onFilter}
          label="Filter requests"
        />
      </div>
      {error && <Notice tone="rose">{error}</Notice>}
      <Card padded={false} className="overflow-hidden">
        {!rows && !error ? (
          <LoadingState label="Loading requests…" />
        ) : rows?.length === 0 ? (
          <EmptyState
            compact
            icon={ForumOutlinedIcon}
            title={search || filter !== "all" ? "Nothing matches that." : "No requests in this period."}
            description="Every request from the app's Ask Assistant page and from Siri shows up here."
          />
        ) : (
          <ul className="divide-y divide-[color:var(--app-divider)]">
            {(rows || []).map((row) => (
              <RequestRow key={row.id} row={row} />
            ))}
          </ul>
        )}
      </Card>
      {hasMore && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            className="app-btn app-btn-secondary"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </>
  );
};

// ------------------------------------------------------- commands, people

const CommandsPanel = ({ commands, requests }) => {
  const shown = commands.filter((c) => c.name !== "assistant_note_unsupported");
  if (!shown.length) {
    return (
      <Card>
        <EmptyState compact icon={GraphicEqRoundedIcon} title="No commands used in this period." />
      </Card>
    );
  }
  const most = shown[0].count;
  return (
    <Card padded={false} className="overflow-x-auto">
      <table className="app-table w-full text-sm">
        <thead>
          <tr>
            <th className="text-left">Command</th>
            <th className="!text-right">Requests</th>
            <th className="!text-right">Failed</th>
            <th className="w-2/5" aria-label="Share of requests" />
          </tr>
        </thead>
        <tbody>
          {shown.map((c) => (
            <tr key={c.name}>
              <td className="font-medium">{commandLabel(c.name)}</td>
              <td className="text-right tabular-nums">{c.count}</td>
              <td
                className={`text-right tabular-nums ${
                  c.failed ? "text-rose-600 dark:text-rose-300" : "app-muted"
                }`}
              >
                {c.failed}
              </td>
              <td>
                <span className="flex items-center gap-2">
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-[color:var(--app-hover)]">
                    <span
                      className="block h-full rounded-full bg-sky-500"
                      style={{ width: `${(c.count / most) * 100}%` }}
                    />
                  </span>
                  <span className="app-muted w-10 text-right text-xs tabular-nums">
                    {percent(c.count, requests)}%
                  </span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};

const PeoplePanel = ({ people }) =>
  people.length === 0 ? (
    <Card>
      <EmptyState compact icon={GraphicEqRoundedIcon} title="Nobody used the assistant in this period." />
    </Card>
  ) : (
    <Card padded={false} className="overflow-x-auto">
      <table className="app-table w-full text-sm">
        <thead>
          <tr>
            <th className="text-left">Person</th>
            <th className="!text-right">Requests</th>
            <th className="!text-right">Siri</th>
            <th className="!text-right">Couldn't do</th>
            <th className="!text-right">Last used</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.email}>
              <td>
                <span className="block font-medium">{p.name}</span>
                <span className="app-muted block text-xs">{p.email}</span>
              </td>
              <td className="text-right tabular-nums">{p.requests}</td>
              <td className="text-right tabular-nums">{p.siri}</td>
              <td className="text-right tabular-nums">{p.unsupported}</td>
              <td className="app-muted whitespace-nowrap text-right">{formatDateTime(p.lastAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );

// ------------------------------------------------------------------- page

const submittedHint = ({ total, byKind }) => {
  if (!total) return "Order Problems and Safety Concerns";
  return Object.entries(byKind)
    .map(([kind, n]) => plural(n, SUBMITTED_LABELS[kind] || "other"))
    .join(", ");
};

const AssistantUsage = () => {
  const user = useSelector(selectUser);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("unsupported");
  const [requestFilter, setRequestFilter] = useState("all");

  const load = useCallback(() => {
    if (!user?.id) return;
    setError(null);
    fetchAssistantUsage(user.id, days)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [user?.id, days]);

  useEffect(() => load(), [load]);

  const showRequests = (filter) => {
    setRequestFilter(filter);
    setTab("requests");
  };

  const totals = data?.totals;
  const tabs = [
    { value: "unsupported", label: "Couldn't do", count: totals?.unsupported },
    { value: "requests", label: "Requests", count: totals?.requests },
    { value: "commands", label: "Commands" },
    { value: "people", label: "People", count: totals?.people },
  ];

  return (
    <PageShell size="lg">
      <PageHeader
        icon={GraphicEqRoundedIcon}
        tone="violet"
        eyebrow="Admin"
        eyebrowTo="/admin"
        title="Assistant Usage"
        description="How people use the Node Mobile assistant and Siri, and what they ask for that it can't do yet."
        actions={<Segmented options={RANGES} value={days} onChange={setDays} label="Time period" />}
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!data && !error && <LoadingState label="Loading assistant usage…" />}

      {data && (
        <>
          {data.truncated && (
            <Notice tone="amber">
              Only the most recent 50,000 rows are counted. Pick a shorter time period for exact numbers.
            </Notice>
          )}

          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatTile
              label="Requests"
              value={totals.requests}
              hint={plural(totals.people, "person", "people")}
              onClick={() => showRequests("all")}
            />
            <StatTile
              label="Through Siri"
              value={totals.siri}
              tone="violet"
              hint={`${percent(totals.siri, totals.requests)}% of requests`}
              onClick={() => showRequests("siri")}
            />
            <StatTile
              label="Couldn't do"
              value={totals.unsupported}
              tone={totals.unsupported ? "amber" : "default"}
              hint="Asked for something new"
              onClick={() => setTab("unsupported")}
            />
            <StatTile
              label="Submitted"
              value={totals.submitted.total}
              tone={totals.submitted.total ? "emerald" : "default"}
              hint={submittedHint(totals.submitted)}
            />
            <StatTile
              label="Problems"
              value={totals.problems}
              tone={totals.problems ? "rose" : "default"}
              hint="Errors or failed commands"
              onClick={() => showRequests("problems")}
            />
            <StatTile
              label="Estimated cost"
              value={`$${totals.costUsd.toFixed(2)}`}
              hint={totals.avgSeconds ? `${totals.avgSeconds}s average answer` : "OpenAI usage"}
            />
          </div>

          {totals.requests > 0 && <PerDayChart byDay={data.byDay} />}

          <div className="mb-4">
            <Segmented options={tabs} value={tab} onChange={setTab} label="Assistant usage sections" />
          </div>

          {tab === "unsupported" && (
            <UnsupportedPanel
              groups={data.unsupported}
              ranNothing={totals.ranNothing}
              onShowRequests={() => showRequests("nothing")}
            />
          )}
          {tab === "requests" && (
            <RequestsPanel
              userId={user?.id}
              days={days}
              filter={requestFilter}
              onFilter={setRequestFilter}
            />
          )}
          {tab === "commands" && <CommandsPanel commands={data.commands} requests={totals.requests} />}
          {tab === "people" && <PeoplePanel people={data.people} />}

          <div className="app-muted mt-6 space-y-1 text-xs">
            <p>
              Estimated cost is OpenAI's list price for the tokens used, plus about 1.5¢ per minute of
              spoken replies.
            </p>
            <p>
              Order Problem requests are anonymous: they're counted, but who asked and what they said
              aren't kept.
            </p>
            <p>
              Siri requests that never reach Node aren't counted, for example when Siri didn't hear "ask
              Node" or the phone isn't signed in to Node Mobile.
            </p>
            <p>
              Couldn't do, Submitted and the spoken-reply part of the cost only cover requests made after
              the assistant started recording them (late October 2026).
            </p>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default AssistantUsage;
